import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  subMonths,
  subWeeks,
} from "date-fns";
import {
  CalendarDays,
  Check,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Copy,
  Link2,
  MoreHorizontal,
  MousePointerClick,
  Lock,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import EditWorkoutView from "@/components/workouts/editWorkoutView";
import ExerciseSelectList from "@/components/workouts/ExerciseSelectList";
import WorkoutEditorWorkspace from "@/components/workouts/WorkoutEditorWorkspace";
import { createWorkoutExercise } from "@/components/workouts/workoutEditorUtils";
import ImportWorkoutDialog from "@/components/workouts/importWorkoutDialog";
import { Workout, Exercise, ExerciseSet } from "@/components/workouts/workoutsColumns";
import { Tables } from "@/lib/supabase/supabaseTypes";
import { Exercise as LibraryExercise } from "@/components/exercises/exerciseColumns";
import {
  AutoPeriodiseExerciseUpdate,
  PeriodisationMatrix,
  PeriodisationScheduleItem,
} from "@/components/training/PeriodisationMatrix";
import { cn } from "@/lib/utils/ui";
import { invokeEdgeFunction } from "@/lib/supabase/edgeFunctions";
import { useOrg } from "@/lib/states/session/hooks";
import { useWeekStartsOn } from "@/lib/dates/useWeekStartsOn";
import {
  dateFromFixedDayIndex,
  endOfWeek as endOfDisplayWeek,
  fixedDayIndexFromDate,
  fixedVisualDays,
  isFixedDayIndexInView,
  startOfWeek as startOfDisplayWeek,
  visualWeekdayIndex,
  weekdayLabels,
} from "@/lib/dates/weekStart";
import { useVersionHistoryPreview } from "@/components/planHistory/VersionHistoryHost";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  useDndContext,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useFeatureFlagEnabled } from "@/components/PostHog/context";

type CalendarMode = "month" | "week";
type ScheduleContext = "client-fixed" | "client-dated" | "template-relative";
type SelectAllByNameSuggestion = {
  key: string;
  displayName: string;
  count: number;
};

export type CalendarScheduleItem = {
  id: string;
  workout_id: string;
  scheduled_date: string;
  order_index: number;
};

export type FixedScheduleItem = {
  id: string;
  workout_id: string;
  day_index: number;
  order_index: number;
  rolling_period?: number;
};

export type RelativeScheduleItem = {
  id: string;
  workout_id: string;
  day_offset: number;
  order_index: number;
};

type ClientTrainingCalendarViewProps = {
  clientId?: string;
  planSchedulingMode: "fixed" | "dated";
  scheduleContext?: ScheduleContext;
  workouts: Workout[];
  scheduleItems: CalendarScheduleItem[];
  fixedScheduleItems: FixedScheduleItem[];
  relativeScheduleItems?: RelativeScheduleItem[];
  fixedScheduleViewWeeks: number;
  relativePageSize?: number;
  relativePageStartOffset?: number;
  hasChanges: boolean;
  setHasChanges: (value: boolean) => void;
  onScheduleItemsChange: (items: CalendarScheduleItem[]) => void;
  onFixedScheduleItemsChange: (items: FixedScheduleItem[]) => void;
  onRelativeScheduleItemsChange?: (items: RelativeScheduleItem[]) => void;
  onCreateWorkoutForDate: (
    scheduledDate: string,
    workoutName: string,
  ) => string | undefined;
  onImportWorkoutForDate: (scheduledDate: string, workout: Workout) => void;
  onCopyWorkoutForDate: (scheduledDate: string, workout: Workout) => void;
  onCreateWorkoutForDay: (
    dayIndex: number,
    workoutName: string,
  ) => string | undefined;
  onImportWorkoutForDay: (dayIndex: number, workout: Workout) => void;
  onCopyWorkoutForDay: (dayIndex: number, workout: Workout) => void;
  onAddExistingWorkoutToDay: (dayIndex: number, workout: Workout) => void;
  onFixedScheduleViewWeeksChange: (weeks: number) => void;
  onRelativePageStartOffsetChange?: (offset: number) => void;
  onSave: () => void;
  saveDisabled?: boolean;
  onDeletePlan?: () => void;
  isTemplateLinked?: boolean;
  onOpenLinkedTemplate?: () => void;
  onWorkoutUpdated: (workout: Workout) => void;
  onDeleteWorkout: (workout: Workout) => void;
  onRemoveExerciseFromWorkout: (workoutId: string, exercise: Exercise) => void;
};

const RELATIVE_BASE_DATE = startOfDay(new Date(2000, 0, 3));
const PERIODISATION_SWEEP_DURATION_MS = 4200;

type EdgeTrackingType =
  | "strength"
  | "bodyweight"
  | "timed"
  | "distance_short"
  | "distance_long";
type EdgeSet = {
  id: string;
  startRange?: number;
  endRange?: number;
  targetWeight?: number;
  targetTimeSeconds?: number;
  targetDistanceMeters?: number;
};
type EdgeRequest = {
  orgId: string;
  selectedItems: PeriodisationScheduleItem[];
  workouts: Array<{
    id: string;
    name: string;
    exercises: Array<{
      id: string;
      name: string;
      tracking_type: EdgeTrackingType;
      occurrence: number;
      sets: EdgeSet[];
    }>;
  }>;
};
type EdgeResponse = {
  updates: AutoPeriodiseExerciseUpdate[];
  skipped: Array<{
    workoutId: string;
    exerciseId: string;
    occurrence: number;
    reasonCode: string;
    reasonText: string;
  }>;
  summary: string;
  meta: {
    planner: "llm" | "fallback";
    model: string;
    version: "v1";
  };
};

const toDateKey = (date: Date) => format(date, "yyyy-MM-dd");

const normalizeScheduleForDate = (
  items: CalendarScheduleItem[],
  scheduledDate: string,
) => {
  const dateItems = items
    .filter((item) => item.scheduled_date === scheduledDate)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    .map((item, index) => ({ ...item, order_index: index }));

  const other = items.filter((item) => item.scheduled_date !== scheduledDate);
  return [...other, ...dateItems];
};

export function ClientTrainingCalendarView({
  clientId,
  planSchedulingMode,
  scheduleContext,
  workouts,
  scheduleItems,
  fixedScheduleItems,
  relativeScheduleItems = [],
  fixedScheduleViewWeeks,
  relativePageSize = 30,
  relativePageStartOffset,
  hasChanges,
  setHasChanges,
  onScheduleItemsChange,
  onFixedScheduleItemsChange,
  onRelativeScheduleItemsChange,
  onCreateWorkoutForDate,
  onImportWorkoutForDate,
  onCopyWorkoutForDate,
  onCreateWorkoutForDay,
  onImportWorkoutForDay,
  onCopyWorkoutForDay,
  onAddExistingWorkoutToDay,
  onFixedScheduleViewWeeksChange,
  onRelativePageStartOffsetChange,
  onSave,
  saveDisabled = false,
  onDeletePlan,
  isTemplateLinked = false,
  onOpenLinkedTemplate,
  onWorkoutUpdated,
  onDeleteWorkout,
  onRemoveExerciseFromWorkout,
}: ClientTrainingCalendarViewProps) {
  const isAiAutoPeriodiseEnabled = useFeatureFlagEnabled("ai-auto-periodise");
  const org = useOrg();
  const weekStartsOn = useWeekStartsOn();
  const weekdayHeaderLabels = useMemo(
    () => weekdayLabels(weekStartsOn),
    [weekStartsOn],
  );
  const resolvedScheduleContext: ScheduleContext =
    scheduleContext ??
    (planSchedulingMode === "fixed" ? "client-fixed" : "client-dated");
  const isFixedMode = resolvedScheduleContext === "client-fixed";
  const isClientDatedMode = resolvedScheduleContext === "client-dated";
  const isTemplateRelativeMode =
    resolvedScheduleContext === "template-relative";
  const isPreviewing = useVersionHistoryPreview();
  const isReadOnlyLinkedPlan = isTemplateLinked || isPreviewing;
  const [mode, setMode] = useState<CalendarMode>("month");
  const [anchorDate, setAnchorDate] = useState<Date>(startOfDay(new Date()));
  const [selectedDateForCreate, setSelectedDateForCreate] = useState<
    string | null
  >(null);
  const [selectedFixedDayForSchedule, setSelectedFixedDayForSchedule] =
    useState<number | null>(null);
  const [relativePageStartOffsetState, setRelativePageStartOffsetState] =
    useState(relativePageStartOffset ?? 0);
  const [newWorkoutName, setNewWorkoutName] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [editingScheduleItemId, setEditingScheduleItemId] = useState<
    string | null
  >(null);
  const [activeScheduleItemId, setActiveScheduleItemId] = useState<
    string | null
  >(null);
  const [copyingScheduleItemId, setCopyingScheduleItemId] = useState<
    string | null
  >(null);
  const [periodisationSelectMode, setPeriodisationSelectMode] = useState(false);
  const [selectedPeriodisationItemIds, setSelectedPeriodisationItemIds] =
    useState<string[]>([]);
  const [periodisationBoxSelectRect, setPeriodisationBoxSelectRect] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const [isPeriodisationBoxSelecting, setIsPeriodisationBoxSelecting] =
    useState(false);
  const [
    hasPeriodisationBoxSelectionStarted,
    setHasPeriodisationBoxSelectionStarted,
  ] = useState(false);
  const [showSelectAllByNameSuggestion, setShowSelectAllByNameSuggestion] =
    useState(false);
  const [renderSelectAllByNameSuggestion, setRenderSelectAllByNameSuggestion] =
    useState<SelectAllByNameSuggestion | null>(null);
  const [activeSelectAllSuggestionKey, setActiveSelectAllSuggestionKey] =
    useState<string | null>(null);
  const [
    dismissedSelectAllSuggestionKeys,
    setDismissedSelectAllSuggestionKeys,
  ] = useState<string[]>([]);
  const [showPeriodisationActionBar, setShowPeriodisationActionBar] =
    useState(false);
  const [isPeriodisationActionBarVisible, setIsPeriodisationActionBarVisible] =
    useState(false);
  const [periodisationDialogOpen, setPeriodisationDialogOpen] = useState(false);
  const [periodisationSweepActive, setPeriodisationSweepActive] = useState(false);
  const [periodisationSweepMotion, setPeriodisationSweepMotion] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const calendarGridRef = useRef<HTMLDivElement | null>(null);
  const boxSelectPointerIdRef = useRef<number | null>(null);
  const boxSelectOriginGridRef = useRef<{ x: number; y: number } | null>(null);
  const boxSelectLastClientRef = useRef<{ x: number; y: number } | null>(null);
  const boxSelectStartClientRef = useRef<{ x: number; y: number } | null>(null);
  const boxSelectScrollContainerRef = useRef<HTMLElement | null>(null);
  const boxSelectAutoScrollRafRef = useRef<number | null>(null);
  const selectAllByNameSuggestionTimeoutRef = useRef<number | null>(null);
  const selectAllByNameSuggestionHideTimeoutRef = useRef<number | null>(null);
  const periodisationActionBarTimeoutRef = useRef<number | null>(null);
  const editingWorkoutBottomRef = useRef<HTMLDivElement | null>(null);
  const [badgeCenterX, setBadgeCenterX] = useState<number | null>(null);

  const workoutById = useMemo(
    () => new Map(workouts.map((workout) => [workout.id, workout])),
    [workouts],
  );
  const effectiveRelativePageStartOffset =
    relativePageStartOffset ?? relativePageStartOffsetState;
  const setEffectiveRelativePageStartOffset = useCallback(
    (nextOffset: number) => {
      const normalized = Math.max(0, nextOffset);
      if (relativePageStartOffset === undefined) {
        setRelativePageStartOffsetState(normalized);
      }
      onRelativePageStartOffsetChange?.(normalized);
    },
    [onRelativePageStartOffsetChange, relativePageStartOffset],
  );

  const fixedWeekStart = useMemo(
    () => startOfDisplayWeek(anchorDate, 1),
    [anchorDate],
  );
  const fixedVisibleDays = useMemo(
    () => fixedScheduleViewWeeks * 7,
    [fixedScheduleViewWeeks],
  );
  const fixedDateFromDayIndex = useCallback(
    (dayIndex: number) =>
      toDateKey(dateFromFixedDayIndex(fixedWeekStart, dayIndex)),
    [fixedWeekStart],
  );
  const fixedDayIndexFromDateKey = useCallback(
    (dateKey: string) =>
      fixedDayIndexFromDate(fixedWeekStart, new Date(dateKey)),
    [fixedWeekStart],
  );
  const isFixedDateSchedulable = useCallback(
    (dateKey: string) =>
      isFixedDayIndexInView(
        fixedDayIndexFromDateKey(dateKey),
        fixedVisibleDays,
      ),
    [fixedDayIndexFromDateKey, fixedVisibleDays],
  );
  const relativeDateFromDayOffset = useCallback(
    (dayOffset: number) => toDateKey(addDays(RELATIVE_BASE_DATE, dayOffset)),
    [],
  );
  const relativeDayOffsetFromDateKey = useCallback(
    (dateKey: string) =>
      Math.max(
        0,
        differenceInCalendarDays(new Date(dateKey), RELATIVE_BASE_DATE),
      ),
    [],
  );
  const scheduleSlotFromDateKey = useCallback(
    (dateKey: string) => {
      if (isFixedMode) return fixedDayIndexFromDateKey(dateKey);
      if (isTemplateRelativeMode) return relativeDayOffsetFromDateKey(dateKey);
      return 0;
    },
    [
      fixedDayIndexFromDateKey,
      isFixedMode,
      isTemplateRelativeMode,
      relativeDayOffsetFromDateKey,
    ],
  );
  const normalizeRelativeScheduleForOffset = useCallback(
    (items: RelativeScheduleItem[], dayOffset: number) => {
      const dayItems = items
        .filter((item) => item.day_offset === dayOffset)
        .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
        .map((item, index) => ({ ...item, order_index: index }));
      const other = items.filter((item) => item.day_offset !== dayOffset);
      return [...other, ...dayItems];
    },
    [],
  );
  const commitDisplayScheduleItems = useCallback(
    (
      items: CalendarScheduleItem[],
      dayKeysToNormalize: string[],
      options?: { preservePeriodisationSelection?: boolean },
    ) => {
      if (isFixedMode) {
        onFixedScheduleItemsChange(
          items.map((item) => ({
            id: item.id,
            workout_id: item.workout_id,
            day_index: scheduleSlotFromDateKey(item.scheduled_date),
            order_index: item.order_index ?? 0,
            rolling_period: fixedVisibleDays,
          })),
        );
      } else if (isTemplateRelativeMode) {
        if (!onRelativeScheduleItemsChange) return;
        let mappedItems = items.map((item) => ({
          id: item.id,
          workout_id: item.workout_id,
          day_offset: relativeDayOffsetFromDateKey(item.scheduled_date),
          order_index: item.order_index ?? 0,
        }));
        dayKeysToNormalize.forEach((dayKey) => {
          mappedItems = normalizeRelativeScheduleForOffset(
            mappedItems,
            relativeDayOffsetFromDateKey(dayKey),
          );
        });
        onRelativeScheduleItemsChange(mappedItems);
      } else {
        let normalizedItems = [...items];
        dayKeysToNormalize.forEach((dayKey) => {
          normalizedItems = normalizeScheduleForDate(normalizedItems, dayKey);
        });
        onScheduleItemsChange(normalizedItems);
      }
      if (!options?.preservePeriodisationSelection) {
        setSelectedPeriodisationItemIds((prev) =>
          prev.filter((id) => items.some((item) => item.id === id)),
        );
      }
    },
    [
      fixedVisibleDays,
      isFixedMode,
      isTemplateRelativeMode,
      normalizeRelativeScheduleForOffset,
      onFixedScheduleItemsChange,
      onRelativeScheduleItemsChange,
      onScheduleItemsChange,
      relativeDayOffsetFromDateKey,
      scheduleSlotFromDateKey,
    ],
  );

  const displayScheduleItems = useMemo<CalendarScheduleItem[]>(
    () =>
      isFixedMode
        ? fixedScheduleItems.map((item) => ({
            id: item.id,
            workout_id: item.workout_id,
            order_index: item.order_index ?? 0,
            scheduled_date: fixedDateFromDayIndex(item.day_index ?? 0),
          }))
        : isTemplateRelativeMode
          ? relativeScheduleItems.map((item) => ({
              id: item.id,
              workout_id: item.workout_id,
              order_index: item.order_index ?? 0,
              scheduled_date: relativeDateFromDayOffset(item.day_offset ?? 0),
            }))
          : scheduleItems,
    [
      fixedDateFromDayIndex,
      fixedScheduleItems,
      isFixedMode,
      isTemplateRelativeMode,
      relativeDateFromDayOffset,
      relativeScheduleItems,
      scheduleItems,
    ],
  );

  const scheduleByDate = useMemo(() => {
    const grouped = new Map<string, CalendarScheduleItem[]>();
    displayScheduleItems.forEach((item) => {
      const list = grouped.get(item.scheduled_date) ?? [];
      list.push(item);
      grouped.set(item.scheduled_date, list);
    });
    grouped.forEach((list, key) => {
      grouped.set(
        key,
        [...list].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)),
      );
    });
    return grouped;
  }, [displayScheduleItems]);

  const editingScheduleItem = useMemo(
    () =>
      displayScheduleItems.find((item) => item.id === editingScheduleItemId) ??
      null,
    [displayScheduleItems, editingScheduleItemId],
  );

  const editingWorkout = useMemo(
    () =>
      editingScheduleItem
        ? (workoutById.get(editingScheduleItem.workout_id) ?? null)
        : null,
    [editingScheduleItem, workoutById],
  );
  const copyingWorkout = useMemo(() => {
    if (!copyingScheduleItemId) return null;
    const item = displayScheduleItems.find(
      (entry) => entry.id === copyingScheduleItemId,
    );
    if (!item) return null;
    return workoutById.get(item.workout_id) ?? null;
  }, [copyingScheduleItemId, displayScheduleItems, workoutById]);
  const editingScheduleDateLabel = useMemo(() => {
    if (!editingScheduleItem?.scheduled_date) return "";
    if (isTemplateRelativeMode) {
      return `Day ${relativeDayOffsetFromDateKey(editingScheduleItem.scheduled_date) + 1}`;
    }
    return format(
      new Date(`${editingScheduleItem.scheduled_date}T00:00:00`),
      "EEE, MMM d",
    );
  }, [
    editingScheduleItem?.scheduled_date,
    isTemplateRelativeMode,
    relativeDayOffsetFromDateKey,
  ]);
  const selectedPeriodisationItems = useMemo(
    () =>
      displayScheduleItems
        .filter((item) => selectedPeriodisationItemIds.includes(item.id))
        .sort((a, b) => {
          if (a.scheduled_date !== b.scheduled_date) {
            return a.scheduled_date.localeCompare(b.scheduled_date);
          }
          return (a.order_index ?? 0) - (b.order_index ?? 0);
        }),
    [displayScheduleItems, selectedPeriodisationItemIds],
  );
  const selectAllByNameSuggestion =
    useMemo<SelectAllByNameSuggestion | null>(() => {
      if (!periodisationSelectMode || selectedPeriodisationItems.length < 2) {
        return null;
      }

      const nameCounts = new Map<
        string,
        { displayName: string; count: number }
      >();
      selectedPeriodisationItems.forEach((item) => {
        const workout = workoutById.get(item.workout_id);
        if (!workout) return;
        const trimmedName = workout.name.trim();
        if (!trimmedName) return;
        const key = trimmedName.toLowerCase();
        const existing = nameCounts.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          nameCounts.set(key, { displayName: trimmedName, count: 1 });
        }
      });

      const best = Array.from(nameCounts.entries())
        .map(([key, value]) => ({
          key,
          displayName: value.displayName,
          count: value.count,
        }))
        .sort((a, b) => b.count - a.count)[0];

      if (!best || best.count < 2) return null;
      return best;
    }, [periodisationSelectMode, selectedPeriodisationItems, workoutById]);
  const selectAllByNameSuggestionKey = selectAllByNameSuggestion?.key ?? null;
  const resolvePeriodisationBoxSelectItemIds = useCallback(
    (selectionRect: {
      left: number;
      top: number;
      right: number;
      bottom: number;
    }) => {
      const node = calendarGridRef.current;
      if (!node) return [];
      const cards = node.querySelectorAll<HTMLElement>(
        "[data-periodisation-item-id]",
      );
      const selectedIds: string[] = [];
      cards.forEach((card) => {
        const itemId = card.dataset.periodisationItemId;
        if (!itemId) return;
        const rect = card.getBoundingClientRect();
        const intersects =
          rect.left < selectionRect.right &&
          rect.right > selectionRect.left &&
          rect.top < selectionRect.bottom &&
          rect.bottom > selectionRect.top;
        if (intersects) {
          selectedIds.push(itemId);
        }
      });
      return selectedIds;
    },
    [],
  );
  const calendarDays = useMemo(() => {
    if (isFixedMode) {
      return fixedVisualDays(
        fixedWeekStart,
        fixedVisibleDays,
        weekStartsOn,
      );
    }
    if (isTemplateRelativeMode) {
      return Array.from({ length: relativePageSize }, (_, idx) =>
        addDays(RELATIVE_BASE_DATE, effectiveRelativePageStartOffset + idx),
      );
    }
    if (mode === "week") {
      const weekStart = startOfDisplayWeek(anchorDate, weekStartsOn);
      return Array.from({ length: 7 }, (_, idx) => addDays(weekStart, idx));
    }

    const monthStart = startOfMonth(anchorDate);
    const monthEnd = endOfMonth(anchorDate);
    const gridStart = startOfDisplayWeek(monthStart, weekStartsOn);
    const gridEnd = endOfDisplayWeek(monthEnd, weekStartsOn);

    const days: Date[] = [];
    for (let date = gridStart; date <= gridEnd; date = addDays(date, 1)) {
      days.push(date);
    }
    return days;
  }, [
    anchorDate,
    effectiveRelativePageStartOffset,
    fixedVisibleDays,
    fixedWeekStart,
    isFixedMode,
    isTemplateRelativeMode,
    mode,
    relativePageSize,
    weekStartsOn,
  ]);
  const weekdayItemIdsInView = useMemo(() => {
    const grouped = new Map<number, string[]>();
    calendarDays.forEach((day) => {
      const weekdayIndex = visualWeekdayIndex(day, weekStartsOn);
      const dateKey = toDateKey(day);
      const dayItems = scheduleByDate.get(dateKey) ?? [];
      const existing = grouped.get(weekdayIndex) ?? [];
      grouped.set(weekdayIndex, [
        ...existing,
        ...dayItems.map((item) => item.id),
      ]);
    });
    return grouped;
  }, [
    anchorDate,
    calendarDays,
    isFixedMode,
    isTemplateRelativeMode,
    mode,
    scheduleByDate,
    weekStartsOn,
  ]);
  const weekdaySelectionState = useMemo(() => {
    const selected = new Set(selectedPeriodisationItemIds);
    return weekdayHeaderLabels.map((_, weekdayIndex) => {
      const ids = weekdayItemIdsInView.get(weekdayIndex) ?? [];
      const selectedCount = ids.filter((id) => selected.has(id)).length;
      return {
        allSelected: ids.length > 0 && selectedCount === ids.length,
        partiallySelected: selectedCount > 0 && selectedCount < ids.length,
      };
    });
  }, [selectedPeriodisationItemIds, weekdayHeaderLabels, weekdayItemIdsInView]);

  const navigatePrevious = () => {
    if (isTemplateRelativeMode) {
      setEffectiveRelativePageStartOffset(
        effectiveRelativePageStartOffset - relativePageSize,
      );
      return;
    }
    setAnchorDate((prev) =>
      mode === "week" ? subWeeks(prev, 1) : subMonths(prev, 1),
    );
  };

  const navigateNext = () => {
    if (isTemplateRelativeMode) {
      setEffectiveRelativePageStartOffset(
        effectiveRelativePageStartOffset + relativePageSize,
      );
      return;
    }
    setAnchorDate((prev) =>
      mode === "week" ? addWeeks(prev, 1) : addMonths(prev, 1),
    );
  };

  const goToToday = () => {
    setAnchorDate(startOfDay(new Date()));
    window.setTimeout(() => {
      const todayKey = toDateKey(startOfDay(new Date()));
      const node = document.querySelector<HTMLElement>(
        `[data-calendar-day-key="${todayKey}"]`,
      );
      node?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
  };

  const openCreateDialog = (scheduledDate: string) => {
    setSelectedDateForCreate(scheduledDate);
    setNewWorkoutName("");
  };

  const removeScheduleItem = (itemId: string) => {
    if (isReadOnlyLinkedPlan) return;
    const item = displayScheduleItems.find((entry) => entry.id === itemId);
    if (!item) return;
    if (isTemplateRelativeMode) {
      const workout = workoutById.get(item.workout_id);
      if (!workout) return;
      onDeleteWorkout(workout);
      if (copyingScheduleItemId === itemId) {
        setCopyingScheduleItemId(null);
      }
      if (editingScheduleItemId === itemId) {
        setEditingScheduleItemId(null);
      }
      return;
    }
    const next = displayScheduleItems.filter((entry) => entry.id !== itemId);
    commitDisplayScheduleItems(next, [item.scheduled_date]);
    setSelectedPeriodisationItemIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : prev,
    );
    if (copyingScheduleItemId === itemId) {
      setCopyingScheduleItemId(null);
    }
    if (editingScheduleItemId === itemId) {
      setEditingScheduleItemId(null);
    }
  };

  const handleCopyWorkoutToDate = (scheduledDate: string) => {
    if (isReadOnlyLinkedPlan) return;
    if (!copyingScheduleItemId) return;
    const source = displayScheduleItems.find(
      (entry) => entry.id === copyingScheduleItemId,
    );
    if (!source) {
      setCopyingScheduleItemId(null);
      return;
    }
    const sourceWorkout = workoutById.get(source.workout_id);
    if (!sourceWorkout) return;
    if (isFixedMode || isTemplateRelativeMode) {
      if (isFixedMode && !isFixedDateSchedulable(scheduledDate)) return;
      onCopyWorkoutForDay(
        scheduleSlotFromDateKey(scheduledDate),
        sourceWorkout,
      );
    } else {
      onCopyWorkoutForDate(scheduledDate, sourceWorkout);
    }
  };

  const handleDayCellClick = (scheduledDate: string) => {
    if (isReadOnlyLinkedPlan) return;
    if (periodisationSelectMode) return;
    if (copyingScheduleItemId) {
      handleCopyWorkoutToDate(scheduledDate);
      return;
    }
    if (isFixedMode) {
      if (!isFixedDateSchedulable(scheduledDate)) return;
      setSelectedFixedDayForSchedule(scheduleSlotFromDateKey(scheduledDate));
      return;
    }
    openCreateDialog(scheduledDate);
  };

  const togglePeriodisationSelectMode = () => {
    if (isReadOnlyLinkedPlan) return;
    if (periodisationSelectMode) {
      setPeriodisationSelectMode(false);
      setSelectedPeriodisationItemIds([]);
      setPeriodisationBoxSelectRect(null);
      return;
    }
    setPeriodisationSelectMode(true);
    setSelectedPeriodisationItemIds([]);
    setPeriodisationBoxSelectRect(null);
  };

  const togglePeriodisationSelection = (itemId: string) => {
    setSelectedPeriodisationItemIds((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };
  const toggleWeekdayPeriodisationSelection = (weekdayIndex: number) => {
    const itemIds = weekdayItemIdsInView.get(weekdayIndex) ?? [];
    if (itemIds.length === 0) return;
    setSelectedPeriodisationItemIds((prev) => {
      const itemIdSet = new Set(itemIds);
      const allSelected = itemIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !itemIdSet.has(id));
      }
      const merged = new Set(prev);
      itemIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
  };
  const selectAllUpcomingWorkoutsByName = useCallback(() => {
    if (!selectAllByNameSuggestion) return;
    const todayKey = toDateKey(startOfDay(new Date()));
    const matchingIds = displayScheduleItems
      .filter((item) => {
        const workout = workoutById.get(item.workout_id);
        if (!workout) return false;
        const matchesName =
          workout.name.trim().toLowerCase() === selectAllByNameSuggestion.key;
        if (!matchesName) return false;
        if (isClientDatedMode) {
          return item.scheduled_date >= todayKey;
        }
        return true;
      })
      .map((item) => item.id);
    if (matchingIds.length === 0) return;
    setSelectedPeriodisationItemIds((prev) => {
      const merged = new Set(prev);
      matchingIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
    setDismissedSelectAllSuggestionKeys((prev) =>
      prev.includes(selectAllByNameSuggestion.key)
        ? prev
        : [...prev, selectAllByNameSuggestion.key],
    );
    setShowSelectAllByNameSuggestion(false);
  }, [
    displayScheduleItems,
    isClientDatedMode,
    selectAllByNameSuggestion,
    workoutById,
  ]);
  const getClosestVerticalScrollContainer = useCallback(
    (node: HTMLElement | null) => {
      let current = node?.parentElement ?? null;
      while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        const canScrollY =
          (overflowY === "auto" ||
            overflowY === "scroll" ||
            overflowY === "overlay") &&
          current.scrollHeight > current.clientHeight + 1;
        if (canScrollY) return current;
        current = current.parentElement;
      }
      return null;
    },
    [],
  );
  const updatePeriodisationBoxSelection = useCallback(
    (clientX: number, clientY: number) => {
      const origin = boxSelectOriginGridRef.current;
      const gridNode = calendarGridRef.current;
      if (!origin || !gridNode) return;

      const gridRect = gridNode.getBoundingClientRect();
      const localX = clientX - gridRect.left;
      const localY = clientY - gridRect.top;
      const clampedX = Math.min(Math.max(localX, 0), gridRect.width);
      const clampedY = Math.min(Math.max(localY, 0), gridRect.height);

      const left = Math.min(origin.x, clampedX);
      const right = Math.max(origin.x, clampedX);
      const top = Math.min(origin.y, clampedY);
      const bottom = Math.max(origin.y, clampedY);

      setPeriodisationBoxSelectRect({
        left,
        top,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
      });
      setSelectedPeriodisationItemIds(
        resolvePeriodisationBoxSelectItemIds({
          left: gridRect.left + left,
          top: gridRect.top + top,
          right: gridRect.left + right,
          bottom: gridRect.top + bottom,
        }),
      );
    },
    [resolvePeriodisationBoxSelectItemIds],
  );
  const handleCalendarGridPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (!periodisationSelectMode || isReadOnlyLinkedPlan) return;
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest("[data-periodisation-weekday-header]")) return;
    if (target.closest("[data-periodisation-item-id]")) return;
    const gridNode = calendarGridRef.current;
    if (!gridNode) return;
    const rect = gridNode.getBoundingClientRect();
    const clampedX = Math.min(Math.max(event.clientX, rect.left), rect.right);
    const clampedY = Math.min(Math.max(event.clientY, rect.top), rect.bottom);
    boxSelectPointerIdRef.current = event.pointerId;
    boxSelectOriginGridRef.current = {
      x: clampedX - rect.left,
      y: clampedY - rect.top,
    };
    boxSelectStartClientRef.current = { x: event.clientX, y: event.clientY };
    boxSelectLastClientRef.current = { x: event.clientX, y: event.clientY };
    boxSelectScrollContainerRef.current =
      getClosestVerticalScrollContainer(gridNode);
    setIsPeriodisationBoxSelecting(false);
    setHasPeriodisationBoxSelectionStarted(false);
    setPeriodisationBoxSelectRect(null);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const handleCalendarGridPointerMove = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (boxSelectPointerIdRef.current !== event.pointerId) return;
    const start = boxSelectStartClientRef.current;
    if (!start) return;
    if (!isPeriodisationBoxSelecting) {
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.hypot(dx, dy) < 6) return;
      setIsPeriodisationBoxSelecting(true);
      setHasPeriodisationBoxSelectionStarted(true);
      setSelectedPeriodisationItemIds([]);
    }
    boxSelectLastClientRef.current = { x: event.clientX, y: event.clientY };
    updatePeriodisationBoxSelection(event.clientX, event.clientY);
    event.preventDefault();
  };

  const endCalendarGridBoxSelection = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (boxSelectPointerIdRef.current !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    boxSelectPointerIdRef.current = null;
    boxSelectOriginGridRef.current = null;
    boxSelectStartClientRef.current = null;
    boxSelectLastClientRef.current = null;
    boxSelectScrollContainerRef.current = null;
    setIsPeriodisationBoxSelecting(false);
    setHasPeriodisationBoxSelectionStarted(false);
    setPeriodisationBoxSelectRect(null);
  };

  const updateWorkoutExerciseFromPeriodisation = (
    workoutId: string,
    exerciseId: string,
    updates: {
      sets?: Exercise["sets"];
    },
    occurrence?: number,
  ) => {
    const workout = workoutById.get(workoutId);
    if (!workout) return;

    let changed = false;
    let seenOccurrence = -1;
    const nextCollection = workout.exerciseCollection.map((group) =>
      group.map((exercise) => {
        if (exercise.id !== exerciseId) return exercise;
        seenOccurrence += 1;
        if (occurrence !== undefined && seenOccurrence !== occurrence) return exercise;

        const rebuiltSets = updates.sets ?? exercise.sets;
        changed = true;
        return { ...exercise, sets: rebuiltSets };
      }),
    );

    if (!changed) return;
    onWorkoutUpdated({
      ...workout,
      exerciseCollection: nextCollection,
    });
    setHasChanges(true);
  };

  const updateWorkoutExercisesFromPeriodisationBatch = (
    updates: AutoPeriodiseExerciseUpdate[],
  ) => {
    if (updates.length === 0) return;

    const updatesByWorkout = new Map<string, AutoPeriodiseExerciseUpdate[]>();
    updates.forEach((update) => {
      const existing = updatesByWorkout.get(update.workoutId);
      if (existing) {
        existing.push(update);
      } else {
        updatesByWorkout.set(update.workoutId, [update]);
      }
    });

    let anyChanged = false;
    updatesByWorkout.forEach((workoutUpdates, workoutId) => {
      const workout = workoutById.get(workoutId);
      if (!workout) return;

      const updateByKey = new Map<string, ExerciseSet[]>();
      workoutUpdates.forEach((update) => {
        updateByKey.set(`${update.exerciseId}:${update.occurrence}`, update.sets);
      });

      if (updateByKey.size === 0) return;

      const occurrenceByExerciseId = new Map<string, number>();
      let workoutChanged = false;

      const nextCollection = workout.exerciseCollection.map((group) =>
        group.map((exercise) => {
          const occurrence = occurrenceByExerciseId.get(exercise.id) ?? 0;
          occurrenceByExerciseId.set(exercise.id, occurrence + 1);
          const nextSets = updateByKey.get(`${exercise.id}:${occurrence}`);
          if (!nextSets) return exercise;
          workoutChanged = true;
          return { ...exercise, sets: nextSets };
        }),
      );

      if (!workoutChanged) return;
      anyChanged = true;
      onWorkoutUpdated({
        ...workout,
        exerciseCollection: nextCollection,
      });
    });

    if (anyChanged) {
      setHasChanges(true);
    }
  };

  const generateAutoPeriodisationFromEdge = useCallback(async () => {
    if (!org?.id) {
      toast.error("No active organisation selected");
      throw new Error("No active organisation selected");
    }

    const selectedItems = selectedPeriodisationItems as PeriodisationScheduleItem[];
    const workoutIds = Array.from(new Set(selectedItems.map((item) => item.workout_id)));
    const workoutsPayload = workoutIds
      .map((workoutId) => {
        const workout = workoutById.get(workoutId);
        if (!workout) return null;
        const occurrenceByExerciseId = new Map<string, number>();
        const exercises = workout.exerciseCollection.flat().map((exercise) => {
          const occurrence = occurrenceByExerciseId.get(exercise.id) ?? 0;
          occurrenceByExerciseId.set(exercise.id, occurrence + 1);
          return {
            id: exercise.id,
            name: exercise.name,
            tracking_type: (exercise.tracking_type ?? "strength") as EdgeTrackingType,
            occurrence,
            sets: (exercise.sets ?? []).map((set) => ({
              id: set.id,
              startRange: set.startRange,
              endRange: set.endRange,
              targetWeight: set.targetWeight,
              targetTimeSeconds: set.targetTimeSeconds,
              targetDistanceMeters: set.targetDistanceMeters,
            })),
          };
        });
        return {
          id: workout.id,
          name: workout.name,
          exercises,
        };
      })
      .filter((workout): workout is NonNullable<typeof workout> => Boolean(workout));

    const payload: EdgeRequest = {
      orgId: org.id,
      selectedItems,
      workouts: workoutsPayload,
    };

    const { data, error } = await invokeEdgeFunction<EdgeResponse>(
      "auto-periodise-workouts",
      { body: payload },
    );

    if (error) {
      console.error("Auto periodisation edge call failed", error);
      toast.error("Failed to generate progression");
      throw new Error(error.message ?? "Failed to generate progression");
    }

    if (!data) {
      toast.error("No progression data returned");
      throw new Error("No progression data returned");
    }

    console.log("[auto-periodise-workouts] full response", data);

    if (data.summary) {
      toast.success(data.summary);
    }

    if (data.skipped.length > 0) {
      toast(`${data.skipped.length} items were skipped`);
    }

    return data.updates ?? [];
  }, [org?.id, selectedPeriodisationItems, workoutById]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateBadgeCenter = () => {
      const rect = node.getBoundingClientRect();
      setBadgeCenterX(rect.left + rect.width / 2);
    };

    updateBadgeCenter();
    const observer = new ResizeObserver(updateBadgeCenter);
    observer.observe(node);
    window.addEventListener("resize", updateBadgeCenter);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateBadgeCenter);
    };
  }, []);

  useEffect(() => {
    if (!copyingScheduleItemId) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Enter") {
        event.preventDefault();
        setCopyingScheduleItemId(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [copyingScheduleItemId]);

  useEffect(() => {
    if (!isReadOnlyLinkedPlan) return;
    setActiveScheduleItemId(null);
    setCopyingScheduleItemId(null);
    setPeriodisationSelectMode(false);
    setSelectedPeriodisationItemIds([]);
    setPeriodisationBoxSelectRect(null);
    setPeriodisationDialogOpen(false);
  }, [isReadOnlyLinkedPlan]);

  useEffect(() => {
    if (!periodisationSweepActive) {
      setPeriodisationSweepMotion(false);
      return;
    }
    setPeriodisationSweepMotion(false);
    const raf = window.requestAnimationFrame(() => {
      setPeriodisationSweepMotion(true);
    });
    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [periodisationSweepActive]);

  useEffect(() => {
    if (selectAllByNameSuggestionTimeoutRef.current !== null) {
      window.clearTimeout(selectAllByNameSuggestionTimeoutRef.current);
      selectAllByNameSuggestionTimeoutRef.current = null;
    }
    if (selectAllByNameSuggestionHideTimeoutRef.current !== null) {
      window.clearTimeout(selectAllByNameSuggestionHideTimeoutRef.current);
      selectAllByNameSuggestionHideTimeoutRef.current = null;
    }

    if (
      !periodisationSelectMode ||
      !selectAllByNameSuggestionKey ||
      dismissedSelectAllSuggestionKeys.includes(selectAllByNameSuggestionKey)
    ) {
      setShowSelectAllByNameSuggestion(false);
      if (renderSelectAllByNameSuggestion) {
        selectAllByNameSuggestionHideTimeoutRef.current = window.setTimeout(
          () => {
            setRenderSelectAllByNameSuggestion(null);
            setActiveSelectAllSuggestionKey(null);
            selectAllByNameSuggestionHideTimeoutRef.current = null;
          },
          220,
        );
      } else {
        setActiveSelectAllSuggestionKey(null);
      }
      return;
    }

    if (selectAllByNameSuggestion) {
      setRenderSelectAllByNameSuggestion(selectAllByNameSuggestion);
    }

    if (activeSelectAllSuggestionKey === selectAllByNameSuggestionKey) {
      setShowSelectAllByNameSuggestion(true);
      return;
    }

    setShowSelectAllByNameSuggestion(false);
    selectAllByNameSuggestionTimeoutRef.current = window.setTimeout(() => {
      setActiveSelectAllSuggestionKey(selectAllByNameSuggestionKey);
      setShowSelectAllByNameSuggestion(true);
      selectAllByNameSuggestionTimeoutRef.current = null;
    }, 400);

    return () => {
      if (selectAllByNameSuggestionTimeoutRef.current !== null) {
        window.clearTimeout(selectAllByNameSuggestionTimeoutRef.current);
        selectAllByNameSuggestionTimeoutRef.current = null;
      }
      if (selectAllByNameSuggestionHideTimeoutRef.current !== null) {
        window.clearTimeout(selectAllByNameSuggestionHideTimeoutRef.current);
        selectAllByNameSuggestionHideTimeoutRef.current = null;
      }
    };
  }, [
    activeSelectAllSuggestionKey,
    dismissedSelectAllSuggestionKeys,
    periodisationSelectMode,
    renderSelectAllByNameSuggestion,
    selectAllByNameSuggestion,
    selectAllByNameSuggestionKey,
  ]);

  useEffect(() => {
    if (periodisationSelectMode) return;
    setDismissedSelectAllSuggestionKeys([]);
    setActiveSelectAllSuggestionKey(null);
    setShowSelectAllByNameSuggestion(false);
    setRenderSelectAllByNameSuggestion(null);
  }, [periodisationSelectMode]);

  useEffect(() => {
    if (!periodisationSelectMode || isReadOnlyLinkedPlan) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isTypingTarget =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        target?.isContentEditable;
      if (isTypingTarget) return;

      if (event.key === "Escape") {
        event.preventDefault();
        setPeriodisationSelectMode(false);
        setSelectedPeriodisationItemIds([]);
        setPeriodisationBoxSelectRect(null);
        return;
      }

      if (event.key === "Enter" && selectedPeriodisationItemIds.length > 0) {
        event.preventDefault();
        setPeriodisationDialogOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isReadOnlyLinkedPlan,
    periodisationSelectMode,
    selectedPeriodisationItemIds.length,
  ]);

  useEffect(() => {
    if (periodisationActionBarTimeoutRef.current !== null) {
      window.clearTimeout(periodisationActionBarTimeoutRef.current);
      periodisationActionBarTimeoutRef.current = null;
    }

    if (periodisationSelectMode) {
      setIsPeriodisationActionBarVisible(false);
      setShowPeriodisationActionBar(true);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          setIsPeriodisationActionBarVisible(true);
        });
      });
      return;
    }

    setIsPeriodisationActionBarVisible(false);
    periodisationActionBarTimeoutRef.current = window.setTimeout(() => {
      setShowPeriodisationActionBar(false);
      periodisationActionBarTimeoutRef.current = null;
    }, 220);

    return () => {
      if (periodisationActionBarTimeoutRef.current !== null) {
        window.clearTimeout(periodisationActionBarTimeoutRef.current);
        periodisationActionBarTimeoutRef.current = null;
      }
    };
  }, [periodisationSelectMode]);

  useEffect(() => {
    if (!isPeriodisationBoxSelecting || !hasPeriodisationBoxSelectionStarted) {
      return;
    }

    const runAutoScroll = () => {
      const pointer = boxSelectLastClientRef.current;
      if (!pointer || !isPeriodisationBoxSelecting) return;

      const edge = 84;
      const maxStep = 18;
      let deltaY = 0;
      const scrollContainer = boxSelectScrollContainerRef.current;

      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        if (pointer.y > containerRect.bottom - edge) {
          const ratio = (pointer.y - (containerRect.bottom - edge)) / edge;
          deltaY = Math.ceil(ratio * maxStep);
        } else if (pointer.y < containerRect.top + edge) {
          const ratio = (containerRect.top + edge - pointer.y) / edge;
          deltaY = -Math.ceil(ratio * maxStep);
        }
        if (deltaY !== 0) {
          const before = scrollContainer.scrollTop;
          const maxTop =
            scrollContainer.scrollHeight - scrollContainer.clientHeight;
          scrollContainer.scrollTop = Math.min(
            Math.max(0, before + deltaY),
            maxTop,
          );
          if (scrollContainer.scrollTop !== before) {
            updatePeriodisationBoxSelection(pointer.x, pointer.y);
          }
        }
      } else {
        if (pointer.y > window.innerHeight - edge) {
          const ratio = (pointer.y - (window.innerHeight - edge)) / edge;
          deltaY = Math.ceil(ratio * maxStep);
        } else if (pointer.y < edge) {
          const ratio = (edge - pointer.y) / edge;
          deltaY = -Math.ceil(ratio * maxStep);
        }
        if (deltaY !== 0) {
          const before = window.scrollY;
          window.scrollBy({ top: deltaY });
          if (window.scrollY !== before) {
            updatePeriodisationBoxSelection(pointer.x, pointer.y);
          }
        }
      }

      boxSelectAutoScrollRafRef.current =
        window.requestAnimationFrame(runAutoScroll);
    };

    const handleScroll = () => {
      const pointer = boxSelectLastClientRef.current;
      if (!pointer) return;
      updatePeriodisationBoxSelection(pointer.x, pointer.y);
    };
    const scrollContainer = boxSelectScrollContainerRef.current;

    boxSelectAutoScrollRafRef.current =
      window.requestAnimationFrame(runAutoScroll);
    window.addEventListener("scroll", handleScroll, { passive: true });
    scrollContainer?.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      if (boxSelectAutoScrollRafRef.current !== null) {
        window.cancelAnimationFrame(boxSelectAutoScrollRafRef.current);
        boxSelectAutoScrollRafRef.current = null;
      }
      window.removeEventListener("scroll", handleScroll);
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [
    hasPeriodisationBoxSelectionStarted,
    isPeriodisationBoxSelecting,
    updatePeriodisationBoxSelection,
  ]);

  const handleCreateWorkout = () => {
    if (!selectedDateForCreate) return;
    let createdScheduleItemId: string | undefined;
    if (isFixedMode || isTemplateRelativeMode) {
      createdScheduleItemId = onCreateWorkoutForDay(
        scheduleSlotFromDateKey(selectedDateForCreate),
        newWorkoutName,
      );
    } else {
      createdScheduleItemId = onCreateWorkoutForDate(
        selectedDateForCreate,
        newWorkoutName,
      );
    }
    if (createdScheduleItemId) {
      setEditingScheduleItemId(createdScheduleItemId);
    }
    setSelectedDateForCreate(null);
    setNewWorkoutName("");
  };

  const handleImportSelected = (workout: Workout) => {
    if (isFixedMode && selectedFixedDayForSchedule !== null) {
      onImportWorkoutForDay(selectedFixedDayForSchedule, workout);
      setImportOpen(false);
      setSelectedFixedDayForSchedule(null);
      setNewWorkoutName("");
      return;
    }
    if (!selectedDateForCreate) return;
    if (isTemplateRelativeMode) {
      onImportWorkoutForDay(
        scheduleSlotFromDateKey(selectedDateForCreate),
        workout,
      );
    } else if (!isFixedMode) {
      onImportWorkoutForDate(selectedDateForCreate, workout);
    }
    setImportOpen(false);
    setSelectedDateForCreate(null);
    setNewWorkoutName("");
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 120, tolerance: 6 },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (isReadOnlyLinkedPlan) return;
    setActiveScheduleItemId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (isReadOnlyLinkedPlan) return;
    const { active, over } = event;
    setActiveScheduleItemId(null);
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    const movingItem = displayScheduleItems.find(
      (item) => item.id === activeId,
    );
    if (!movingItem) return;

    const isDayDrop = overId.startsWith("day-");
    const overItem = isDayDrop
      ? null
      : displayScheduleItems.find((item) => item.id === overId);
    const targetDate = isDayDrop
      ? overId.replace("day-", "")
      : overItem?.scheduled_date;
    if (!targetDate) return;
    if (isFixedMode && !isFixedDateSchedulable(targetDate)) return;

    const sourceDate = movingItem.scheduled_date;
    const sourceItems = displayScheduleItems
      .filter((item) => item.scheduled_date === sourceDate)
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
    const withoutMoving = displayScheduleItems.filter(
      (item) => item.id !== activeId,
    );
    const targetItemsWithoutMoving = withoutMoving
      .filter((item) => item.scheduled_date === targetDate)
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

    let targetIndex = targetItemsWithoutMoving.length;
    if (!isDayDrop && overItem) {
      const overIndex = targetItemsWithoutMoving.findIndex(
        (item) => item.id === overItem.id,
      );
      targetIndex =
        overIndex >= 0 ? overIndex : targetItemsWithoutMoving.length;
    }

    let nextItems: CalendarScheduleItem[] = withoutMoving.map((item) => ({
      ...item,
    }));

    if (sourceDate === targetDate) {
      const dayWithoutMoving = sourceItems.filter(
        (item) => item.id !== activeId,
      );
      const insertIndex = Math.max(
        0,
        Math.min(targetIndex, dayWithoutMoving.length),
      );
      const reordered = [...dayWithoutMoving];
      reordered.splice(insertIndex, 0, {
        ...movingItem,
        scheduled_date: targetDate,
      });
      nextItems = [
        ...nextItems.filter((item) => item.scheduled_date !== sourceDate),
        ...reordered,
      ];
      commitDisplayScheduleItems(nextItems, [sourceDate], {
        preservePeriodisationSelection: true,
      });
      return;
    }

    const insertIndex = Math.max(
      0,
      Math.min(targetIndex, targetItemsWithoutMoving.length),
    );
    const targetReordered = [...targetItemsWithoutMoving];
    targetReordered.splice(insertIndex, 0, {
      ...movingItem,
      scheduled_date: targetDate,
    });

    nextItems = [
      ...nextItems.filter((item) => item.scheduled_date !== targetDate),
      ...targetReordered,
    ];

    const normalizedBoth = normalizeScheduleForDate(
      normalizeScheduleForDate(nextItems, sourceDate),
      targetDate,
    );
    commitDisplayScheduleItems(normalizedBoth, [sourceDate, targetDate], {
      preservePeriodisationSelection: true,
    });
  };

  const handleExerciseSelectForEditingWorkout = (
    exercise: Tables<"exercises">,
  ) => {
    if (isReadOnlyLinkedPlan) return;
    if (!editingWorkout) return;
    const nextExercise = createWorkoutExercise({
      exercise,
      exOrder: editingWorkout.exerciseCollection.length,
      subOrder: 0,
    });

    onWorkoutUpdated({
      ...editingWorkout,
      exerciseCollection: [
        ...editingWorkout.exerciseCollection,
        [nextExercise],
      ],
    });
    setHasChanges(true);
    window.setTimeout(() => {
      editingWorkoutBottomRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 120);
  };

  const handleExerciseCreatedForEditingWorkout = (
    exercise: LibraryExercise,
  ) => {
    handleExerciseSelectForEditingWorkout(exercise);
  };
  const selectedCreateLabel = useMemo(() => {
    if (!selectedDateForCreate) return "";
    if (isTemplateRelativeMode) {
      return `${relativeDayOffsetFromDateKey(selectedDateForCreate) + 1}`;
    }
    return format(new Date(selectedDateForCreate), "EEE, MMM d");
  }, [
    isTemplateRelativeMode,
    relativeDayOffsetFromDateKey,
    selectedDateForCreate,
  ]);
  const templateRelativeWeekRangeLabel = useMemo(() => {
    const startWeek = Math.floor(effectiveRelativePageStartOffset / 7) + 1;
    const weeksInPage = Math.max(1, Math.floor(relativePageSize / 7));
    const endWeek = startWeek + weeksInPage - 1;
    return `Weeks ${startWeek} - ${endWeek}`;
  }, [effectiveRelativePageStartOffset, relativePageSize]);

  return (
    <>
      <div ref={containerRef} className="space-y-2 pb-16">
        <div className="sticky top-0 z-30 flex min-h-14 flex-wrap items-center justify-between gap-3 bg-background/95 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="flex flex-wrap items-center gap-2">
            {isClientDatedMode ? (
              <>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={navigatePrevious}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={navigateNext}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            ) : isTemplateRelativeMode ? (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={navigatePrevious}
                  disabled={effectiveRelativePageStartOffset === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={navigateNext}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            ) : null}
            <div className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium">
              <CalendarDays className="h-4 w-4" />
              {isFixedMode
                ? `${fixedScheduleViewWeeks === 2 ? "2-Week" : "1-Week"} Pattern`
                : isTemplateRelativeMode
                  ? templateRelativeWeekRangeLabel
                  : format(anchorDate, "MMM yyyy")}
            </div>
          </div>
          <div className="gap-4 flex flex-row items-center">
            {isTemplateLinked ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => onOpenLinkedTemplate?.()}
                disabled={!onOpenLinkedTemplate}
              >
                <Link2 className="h-3.5 w-3.5" />
                Linked template
              </Button>
            ) : null}
            <div className="inline-flex rounded-md border p-1">
              {!isFixedMode ? (
                <Button
                  size="sm"
                  variant={periodisationSelectMode ? "default" : "ghost"}
                  onClick={togglePeriodisationSelectMode}
                  disabled={
                    Boolean(copyingScheduleItemId) || isReadOnlyLinkedPlan
                  }
                >
                  <CheckSquare className="mr-1 h-4 w-4" />
                  Periodize
                </Button>
              ) : null}
              {isTemplateRelativeMode ? null : (
                <>
                  <Button
                    size="sm"
                    variant={
                      isFixedMode
                        ? fixedScheduleViewWeeks === 1
                          ? "default"
                          : "ghost"
                        : mode === "week"
                          ? "default"
                          : "ghost"
                    }
                    onClick={() => {
                      if (isFixedMode) {
                        onFixedScheduleViewWeeksChange(1);
                      } else {
                        setMode("week");
                      }
                    }}
                  >
                    {isFixedMode ? "1 Week" : "Week"}
                  </Button>
                  <Button
                    size="sm"
                    variant={
                      isFixedMode
                        ? fixedScheduleViewWeeks === 2
                          ? "default"
                          : "ghost"
                        : mode === "month"
                          ? "default"
                          : "ghost"
                    }
                    onClick={() => {
                      if (isFixedMode) {
                        onFixedScheduleViewWeeksChange(2);
                      } else {
                        setMode("month");
                      }
                    }}
                  >
                    {isFixedMode ? "2 Weeks" : "Month"}
                  </Button>
                </>
              )}
            </div>
            {!isReadOnlyLinkedPlan || (isClientDatedMode && onDeletePlan) ? (
              <div className="inline-flex items-center rounded-md border p-1">
                {!isReadOnlyLinkedPlan ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-0 shadow-none"
                    onClick={onSave}
                    disabled={saveDisabled}
                  >
                    Save
                  </Button>
                ) : null}
                {isClientDatedMode && onDeletePlan ? (
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8 border-0 shadow-none"
                      >
                        <span className="sr-only">Calendar plan actions</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => onDeletePlan()}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove plan
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border">
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div
              ref={calendarGridRef}
              className={cn(
                "relative grid min-w-[1200px] grid-cols-7",
                periodisationSelectMode && "select-none",
              )}
              onPointerDown={handleCalendarGridPointerDown}
              onPointerMove={handleCalendarGridPointerMove}
              onPointerUp={endCalendarGridBoxSelection}
              onPointerCancel={endCalendarGridBoxSelection}
            >
              {isTemplateRelativeMode
                ? null
                : weekdayHeaderLabels.map((label, weekdayIndex) => {
                    const selectionState = weekdaySelectionState[weekdayIndex];
                    return (
                      <button
                        key={`${label}-${weekdayIndex}`}
                        type="button"
                        data-periodisation-weekday-header="true"
                        onClick={() => {
                          if (!periodisationSelectMode || isReadOnlyLinkedPlan)
                            return;
                          toggleWeekdayPeriodisationSelection(weekdayIndex);
                        }}
                        title={
                          periodisationSelectMode
                            ? `Toggle ${label} workouts in current view`
                            : undefined
                        }
                        aria-pressed={
                          periodisationSelectMode
                            ? (selectionState?.allSelected ?? false)
                            : undefined
                        }
                        disabled={
                          !periodisationSelectMode || isReadOnlyLinkedPlan
                        }
                        data-periodisation-weekday-select={
                          periodisationSelectMode
                            ? selectionState?.allSelected
                              ? "full"
                              : selectionState?.partiallySelected
                                ? "partial"
                                : "none"
                            : "off"
                        }
                        className={cn(
                          "border-r border-b bg-muted/30 px-2 py-2 text-center text-xs font-medium text-muted-foreground",
                          periodisationSelectMode &&
                            !isReadOnlyLinkedPlan &&
                            "cursor-pointer hover:bg-muted/50",
                          periodisationSelectMode &&
                            selectionState?.allSelected &&
                            "bg-primary/15 text-primary dark:bg-primary/25 dark:text-blue-100",
                          periodisationSelectMode &&
                            selectionState?.partiallySelected &&
                            "bg-primary/10 text-primary/90 dark:bg-primary/20 dark:text-blue-100",
                        )}
                      >
                        {label}
                      </button>
                    );
                  })}

              {calendarDays.map((day) => {
                const dateKey = toDateKey(day);
                const dayItems = scheduleByDate.get(dateKey) ?? [];
                const isToday = isSameDay(day, new Date());
                const isFixedPadding =
                  isFixedMode && !isFixedDateSchedulable(dateKey);
                const isOutside =
                  isFixedPadding ||
                  (!isFixedMode &&
                    !isTemplateRelativeMode &&
                    mode === "month" &&
                    !isSameMonth(day, anchorDate));
                const relativeDayLabel = isTemplateRelativeMode
                  ? relativeDayOffsetFromDateKey(dateKey) + 1
                  : null;

                return (
                  <CalendarDayDropZone
                    key={dateKey}
                    dayKey={dateKey}
                    dataDayKey={dateKey}
                    itemIds={dayItems.map((item) => item.id)}
                    className="group min-h-[220px] border-r border-b pt-2 align-top"
                    onClick={() => {
                      if (isFixedPadding) return;
                      handleDayCellClick(dateKey);
                    }}
                  >
                    <div className="mb-2 flex items-center justify-between px-2">
                      {isFixedMode ? (
                        <span className="h-5" />
                      ) : isTemplateRelativeMode ? (
                        <button
                          type="button"
                          className="text-xs font-medium text-foreground"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDayCellClick(dateKey);
                          }}
                        >
                          {relativeDayLabel}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={`text-sm font-medium ${
                            isOutside
                              ? "text-muted-foreground/50"
                              : "text-foreground"
                          } ${isToday ? "text-primary" : ""}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDayCellClick(dateKey);
                          }}
                        >
                          {format(day, "d")}
                        </button>
                      )}
                      {!isReadOnlyLinkedPlan && !isFixedPadding ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-primary/80 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                          title="Add workout"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleDayCellClick(dateKey);
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>

                    <ScrollArea className="h-[185px]">
                      <SortableContext
                        items={dayItems.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2 pb-2 pl-2 pr-4 pt-1">
                          {dayItems.map((item) => {
                            const workout = workoutById.get(item.workout_id);
                            if (!workout) return null;
                            const exerciseCount =
                              workout.exerciseCollection.flat().length;
                            return (
                              <SortableCalendarWorkoutCard
                                key={item.id}
                                itemId={item.id}
                                name={workout.name}
                                exerciseCount={exerciseCount}
                                isCopySource={copyingScheduleItemId === item.id}
                                isCopyMode={Boolean(copyingScheduleItemId)}
                                isPeriodisationSelectMode={
                                  periodisationSelectMode
                                }
                                isPeriodisationSelected={selectedPeriodisationItemIds.includes(
                                  item.id,
                                )}
                                removeLabel={
                                  isTemplateRelativeMode
                                    ? "Delete"
                                    : isFixedMode
                                      ? "Unschedule"
                                      : "Remove"
                                }
                                allowCopy={
                                  !isFixedMode && !isReadOnlyLinkedPlan
                                }
                                allowRemove={!isReadOnlyLinkedPlan}
                                interactionsDisabled={isReadOnlyLinkedPlan}
                                onOpen={() => {
                                  setEditingScheduleItemId(item.id);
                                }}
                                onCopy={() => setCopyingScheduleItemId(item.id)}
                                onRemove={() => removeScheduleItem(item.id)}
                                onTogglePeriodisationSelect={() =>
                                  togglePeriodisationSelection(item.id)
                                }
                              />
                            );
                          })}
                        </div>
                      </SortableContext>
                      <ScrollBar orientation="vertical" className="mr-1" />
                    </ScrollArea>
                  </CalendarDayDropZone>
                );
              })}
              {periodisationBoxSelectRect ? (
                <div
                  className="pointer-events-none absolute z-20 border-2 border-primary/70 bg-primary/15 dark:bg-primary/20"
                  style={{
                    left: periodisationBoxSelectRect.left,
                    top: periodisationBoxSelectRect.top,
                    width: periodisationBoxSelectRect.width,
                    height: periodisationBoxSelectRect.height,
                  }}
                />
              ) : null}
            </div>
            <DragOverlay>
              {!isReadOnlyLinkedPlan && activeScheduleItemId ? (
                <CalendarWorkoutOverlayCard
                  item={
                    displayScheduleItems.find(
                      (item) => item.id === activeScheduleItemId,
                    ) ?? null
                  }
                  workoutById={workoutById}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
        {copyingScheduleItemId && copyingWorkout ? (
          <div
            className="fixed bottom-6 z-50 flex -translate-x-1/2 items-center gap-3 rounded-md border bg-background/95 px-3 py-2 shadow-md backdrop-blur supports-[backdrop-filter]:bg-background/85"
            style={{ left: badgeCenterX ?? undefined }}
          >
            <span className="text-sm font-medium">
              Copying {copyingWorkout.name}
            </span>
            <Button
              size="sm"
              onClick={() => setCopyingScheduleItemId(null)}
              className="h-7 px-2"
            >
              <Check className="mr-1 h-3 w-3" />
              Done
            </Button>
          </div>
        ) : null}
        {showPeriodisationActionBar ? (
          <div
            className={cn(
              "fixed bottom-6 z-50 -translate-x-1/2 transition-all duration-200",
              isPeriodisationActionBarVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-2 opacity-0 pointer-events-none",
            )}
            style={{ left: badgeCenterX ?? undefined }}
          >
            {renderSelectAllByNameSuggestion ? (
              <button
                type="button"
                onClick={selectAllUpcomingWorkoutsByName}
                className={cn(
                  "pointer-events-auto absolute right-full top-1/2 mr-2 inline-flex -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-md border bg-background/95 px-3 py-2 text-sm font-medium shadow-md backdrop-blur transition-all duration-300 supports-[backdrop-filter]:bg-background/85",
                  showSelectAllByNameSuggestion
                    ? "translate-x-0 opacity-100"
                    : "-translate-x-2 opacity-0 pointer-events-none",
                )}
              >
                <MousePointerClick className="h-4 w-4 text-primary" />
                <span>
                  Select all upcoming{" "}
                  {renderSelectAllByNameSuggestion.displayName} workouts
                </span>
              </button>
            ) : null}
            <div className="pointer-events-auto relative z-10 flex items-center gap-2 rounded-md border bg-background/95 px-3 py-2 shadow-md backdrop-blur supports-[backdrop-filter]:bg-background/85">
              <span className="text-sm font-medium">
                {selectedPeriodisationItemIds.length} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setPeriodisationSelectMode(false);
                  setSelectedPeriodisationItemIds([]);
                  setPeriodisationBoxSelectRect(null);
                }}
              >
                <X className="mr-1 h-3 w-3" />
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={selectedPeriodisationItemIds.length === 0}
                onClick={() => setPeriodisationDialogOpen(true)}
              >
                Confirm
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <Dialog
        open={selectedDateForCreate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedDateForCreate(null);
            setImportOpen(false);
          }
        }}
        analyticsName="client_training_calendar_create_workout_dialog"
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add workout on {selectedCreateLabel}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Workout name"
              value={newWorkoutName}
              onChange={(event) => setNewWorkoutName(event.target.value)}
            />
            <div className="flex gap-2">
              <Button className="flex-1" onClick={handleCreateWorkout}>
                Create Workout
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setImportOpen(true)}
              >
                Import from Library
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ImportWorkoutDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onWorkoutSelected={handleImportSelected}
      />

      <Dialog
        open={isFixedMode && selectedFixedDayForSchedule !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedFixedDayForSchedule(null);
          }
        }}
        analyticsName="client_training_calendar_schedule_workout_dialog"
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add workout to schedule</DialogTitle>
          </DialogHeader>
          {workouts.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No workouts in this plan yet.
            </div>
          ) : (
            <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Schedule Existing
              </div>
              {workouts.map((workout) => (
                <Button
                  key={`fixed-schedule-${workout.id}`}
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    if (selectedFixedDayForSchedule === null) return;
                    onAddExistingWorkoutToDay(
                      selectedFixedDayForSchedule,
                      workout,
                    );
                    setSelectedFixedDayForSchedule(null);
                  }}
                >
                  {workout.name}
                </Button>
              ))}
              <Separator className="my-3" />
              <div className="space-y-2">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Or Create / Import
                </div>
                <Input
                  placeholder="Workout name"
                  value={newWorkoutName}
                  onChange={(event) => setNewWorkoutName(event.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() => {
                      if (selectedFixedDayForSchedule === null) return;
                      const createdScheduleItemId = onCreateWorkoutForDay(
                        selectedFixedDayForSchedule,
                        newWorkoutName,
                      );
                      if (createdScheduleItemId) {
                        setEditingScheduleItemId(createdScheduleItemId);
                      }
                      setSelectedFixedDayForSchedule(null);
                      setNewWorkoutName("");
                    }}
                  >
                    Create Workout
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setImportOpen(true)}
                  >
                    Import from Library
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={editingWorkout !== null}
        onOpenChange={(open) => {
          if (!open) setEditingScheduleItemId(null);
        }}
        analyticsName="client_training_calendar_edit_workout_dialog"
      >
        <DialogContent
          className={cn(
            "h-[92vh] w-[95vw] overflow-hidden p-0",
            isReadOnlyLinkedPlan ? "max-w-[760px]" : "max-w-[1100px]",
          )}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {editingWorkout ? (
            <div className="flex h-full min-h-0 flex-col">
              <DialogHeader className="border-b px-6 pt-4 pb-2">
                <div className="flex flex-col gap-1">
                  <DialogTitle>Edit Workout</DialogTitle>
                  {editingScheduleDateLabel && (
                    <p className="text-muted-foreground text-xs">
                      {editingScheduleDateLabel}
                    </p>
                  )}
                  {isTemplateLinked ? (
                    <div className="mt-1 inline-flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
                      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        Training plan is linked. Unlink this plan to make
                        individual client changes.
                      </span>
                    </div>
                  ) : null}
                </div>
              </DialogHeader>
              <WorkoutEditorWorkspace
                workout={editingWorkout}
                updateWorkout={(nextWorkout) => {
                  if (isReadOnlyLinkedPlan) return;
                  onWorkoutUpdated(nextWorkout);
                  setHasChanges(true);
                }}
                setHasChanges={setHasChanges}
                className="flex min-h-0 flex-1"
              >
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                  <div className="mx-auto w-full max-w-[640px]">
                    <EditWorkoutView
                      workout={editingWorkout}
                      updateWorkout={(nextWorkout) => {
                        if (isReadOnlyLinkedPlan) return;
                        onWorkoutUpdated(nextWorkout);
                        setHasChanges(true);
                      }}
                      name={{
                        value: editingWorkout.name,
                        valueChange: (value) => {
                          if (isReadOnlyLinkedPlan) return;
                          onWorkoutUpdated({ ...editingWorkout, name: value });
                          setHasChanges(true);
                        },
                      }}
                      description={{
                        value: editingWorkout.description ?? "",
                        valueChange: (value) => {
                          if (isReadOnlyLinkedPlan) return;
                          onWorkoutUpdated({
                            ...editingWorkout,
                            description: value,
                          });
                          setHasChanges(true);
                        },
                      }}
                      hasChanges={hasChanges}
                      setHasChanges={setHasChanges}
                      removeExercise={(exercise) =>
                        isReadOnlyLinkedPlan
                          ? undefined
                          : onRemoveExerciseFromWorkout(
                              editingWorkout.id,
                              exercise,
                            )
                      }
                      deleteWorkout={() => {
                        if (isReadOnlyLinkedPlan) return;
                        onDeleteWorkout(editingWorkout);
                        setEditingScheduleItemId(null);
                      }}
                      readOnly={isReadOnlyLinkedPlan}
                      clientId={clientId}
                    />
                    <div ref={editingWorkoutBottomRef} />
                  </div>
                </div>
                {!isReadOnlyLinkedPlan ? (
                  <ExerciseSelectList
                    exerciseSelected={handleExerciseSelectForEditingWorkout}
                    enableDragToWorkout
                    onExerciseCreated={handleExerciseCreatedForEditingWorkout}
                    className="client-chat-hide-exercise-select hidden h-full min-w-[340px] max-w-[380px] flex-1 rounded-none border-b-0 border-r-0 border-t-0 shadow-none xl:flex"
                  />
                ) : null}
              </WorkoutEditorWorkspace>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog
        open={periodisationDialogOpen}
        onOpenChange={setPeriodisationDialogOpen}
        analyticsName="client_training_calendar_periodisation_dialog"
      >
        <DialogContent className="w-[96vw] max-w-[1700px] min-w-0 max-h-[94vh] overflow-hidden p-0 [&>button]:hidden">
          <div className="relative flex min-h-0 min-w-0 max-h-[94vh] flex-col">
            {periodisationSweepActive ? (
              <div className="pointer-events-none absolute inset-0 z-[45] overflow-hidden">
                <div
                  className={cn(
                    "absolute -inset-y-[14%] w-[120%] rounded-[42%] blur-2xl transition-transform ease-out",
                    periodisationSweepMotion
                      ? "translate-x-[185%]"
                      : "-translate-x-full",
                  )}
                  style={{
                    backgroundImage:
                      "linear-gradient(90deg, rgba(139,92,246,0) 0%, rgba(139,92,246,0.28) 28%, rgba(217,70,239,0.32) 52%, rgba(236,72,153,0.2) 68%, rgba(236,72,153,0) 100%), radial-gradient(140px 70px at 22% 48%, rgba(216,180,254,0.45) 0%, rgba(216,180,254,0) 72%), radial-gradient(180px 86px at 45% 55%, rgba(196,181,253,0.4) 0%, rgba(196,181,253,0) 74%), radial-gradient(150px 72px at 70% 46%, rgba(244,114,182,0.3) 0%, rgba(244,114,182,0) 70%)",
                    transitionDuration: `${PERIODISATION_SWEEP_DURATION_MS}ms`,
                  }}
                />
                <div
                  className={cn(
                    "absolute -inset-y-[6%] w-[70%] blur-xl transition-transform ease-out mix-blend-screen",
                    periodisationSweepMotion
                      ? "translate-x-[230%]"
                      : "-translate-x-[130%]",
                  )}
                  style={{
                    backgroundImage:
                      "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(233,213,255,0.34) 45%, rgba(255,255,255,0) 100%)",
                    transitionDuration: `${Math.round(PERIODISATION_SWEEP_DURATION_MS * 0.86)}ms`,
                  }}
                />
              </div>
            ) : null}
            <ScrollArea
              className="min-h-0 min-w-0 w-full h-auto max-h-[82vh]"
            >
              <div className="inline-block min-w-max align-top">
                <PeriodisationMatrix
                  selectedItems={
                    selectedPeriodisationItems as PeriodisationScheduleItem[]
                  }
                  workoutById={workoutById}
                  onChangeExercise={updateWorkoutExerciseFromPeriodisation}
                  onAutoPeriodiseApply={
                    updateWorkoutExercisesFromPeriodisationBatch
                  }
                  onAutoSweepStateChange={setPeriodisationSweepActive}
                  onAutoPeriodiseGenerate={generateAutoPeriodisationFromEdge}
                  showAutoPeriodise={Boolean(isAiAutoPeriodiseEnabled)}
                />
              </div>
              <ScrollBar orientation="horizontal" />
              <ScrollBar orientation="vertical" />
            </ScrollArea>
            <div className="w-full shrink-0 border-t bg-background px-6 py-3">
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPeriodisationDialogOpen(false);
                    setPeriodisationBoxSelectRect(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


type CalendarDayDropZoneProps = {
  dayKey: string;
  dataDayKey?: string;
  itemIds: string[];
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
};

const CalendarDayDropZone = ({
  dayKey,
  dataDayKey,
  itemIds,
  className,
  onClick,
  children,
}: CalendarDayDropZoneProps) => {
  const droppableId = `day-${dayKey}`;
  const { setNodeRef } = useDroppable({ id: droppableId });
  const { over, active } = useDndContext();
  const overId = over ? String(over.id) : "";
  const isOver = overId === droppableId || itemIds.includes(overId);
  const isDragging = Boolean(active);

  return (
    <div
      ref={setNodeRef}
      data-calendar-day-key={dataDayKey}
      className={cn(
        className,
        isOver && "bg-primary/5",
        isDragging && "transition",
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

type SortableCalendarWorkoutCardProps = {
  itemId: string;
  name: string;
  exerciseCount: number;
  isCopySource: boolean;
  isCopyMode: boolean;
  isPeriodisationSelectMode: boolean;
  isPeriodisationSelected: boolean;
  removeLabel?: string;
  allowCopy?: boolean;
  allowRemove?: boolean;
  interactionsDisabled?: boolean;
  onOpen: () => void;
  onCopy: () => void;
  onRemove: () => void;
  onTogglePeriodisationSelect: () => void;
};

const SortableCalendarWorkoutCard = ({
  itemId,
  name,
  exerciseCount,
  isCopySource,
  isCopyMode,
  isPeriodisationSelectMode,
  isPeriodisationSelected,
  removeLabel = "Remove",
  allowCopy = true,
  allowRemove = true,
  interactionsDisabled = false,
  onOpen,
  onCopy,
  onRemove,
  onTogglePeriodisationSelect,
}: SortableCalendarWorkoutCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: itemId,
    disabled: interactionsDisabled || isPeriodisationSelectMode,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ContextMenu modal={false}>
      <ContextMenuTrigger asChild>
        <div
          ref={setNodeRef}
          data-periodisation-item-id={itemId}
          style={style}
          onClick={(event) => {
            event.stopPropagation();
            if (isPeriodisationSelectMode) {
              onTogglePeriodisationSelect();
              return;
            }
            if (isCopyMode) return;
            onOpen();
          }}
          className={cn(
            "group relative rounded-md border border-primary/20 bg-primary/5 px-2 py-1",
            !interactionsDisabled && "cursor-grab active:cursor-grabbing",
            isCopySource &&
              "border-primary ring-2 ring-primary/30 bg-primary/10",
            isPeriodisationSelected &&
              "border-primary ring-2 ring-primary/35 bg-primary/12 dark:bg-primary/20",
            isPeriodisationSelectMode && "cursor-pointer active:cursor-pointer",
            isDragging && "opacity-70",
          )}
        >
          {isPeriodisationSelected ? (
            <span className="absolute right-0.5 top-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Check className="h-3 w-3" />
            </span>
          ) : null}
          <div className="pr-6" {...attributes} {...listeners}>
            <div className="truncate text-xs font-medium">{name}</div>
            <div className="text-[11px] text-muted-foreground">
              {exerciseCount} Exercises
            </div>
          </div>
          <DropdownMenu modal={false}>
            {!isPeriodisationSelectMode && (allowCopy || allowRemove) ? (
              <>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0.5 top-0.5 h-5 w-5 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {allowCopy ? (
                    <>
                      <DropdownMenuItem
                        onClick={(event) => {
                          event.stopPropagation();
                          onCopy();
                        }}
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copy
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  {allowRemove ? (
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {removeLabel}
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </>
            ) : null}
          </DropdownMenu>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        {isPeriodisationSelectMode ? (
          <ContextMenuItem
            onClick={(event) => {
              event.stopPropagation();
              onTogglePeriodisationSelect();
            }}
          >
            <CheckSquare className="mr-2 h-4 w-4" />
            {isPeriodisationSelected ? "Unselect" : "Select"}
          </ContextMenuItem>
        ) : allowCopy ? (
          <ContextMenuItem
            onClick={(event) => {
              event.stopPropagation();
              onCopy();
            }}
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy
          </ContextMenuItem>
        ) : null}
        {allowCopy && !isPeriodisationSelectMode ? (
          <ContextMenuSeparator />
        ) : null}
        {allowRemove ? (
          <ContextMenuItem
            variant="destructive"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {removeLabel}
          </ContextMenuItem>
        ) : null}
      </ContextMenuContent>
    </ContextMenu>
  );
};

type CalendarWorkoutOverlayCardProps = {
  item: CalendarScheduleItem | null;
  workoutById: Map<string, Workout>;
};

const CalendarWorkoutOverlayCard = ({
  item,
  workoutById,
}: CalendarWorkoutOverlayCardProps) => {
  if (!item) return null;
  const workout = workoutById.get(item.workout_id);
  const name = workout?.name ?? "Workout";
  const exerciseCount = workout?.exerciseCollection.flat().length ?? 0;
  return (
    <div className="rounded-md border border-primary/20 bg-background px-2 py-1 shadow-sm">
      <div className="truncate text-xs font-medium">{name}</div>
      <div className="text-[11px] text-muted-foreground">
        {exerciseCount} Exercises
      </div>
    </div>
  );
};
